import { useMutation } from '@tanstack/react-query';
import { orpc, useInvalidateRpcQueryFn } from '@/api/rpc/client';
import type { DeleteAPInvoiceInput, DeleteAPInvoiceResponse } from '@erp/rpc';
import { useToast } from '@/context/ToastContext';

export interface UseDeleteAPInvoiceOptions {
  onSuccess?: (
    data: DeleteAPInvoiceResponse,
    variables: DeleteAPInvoiceInput,
  ) => void;
}

/**
 * Hook for deleting AP invoices.
 * Handles the API call, success/error states, and cache invalidation.
 */
export function useDeleteAPInvoice(options?: UseDeleteAPInvoiceOptions) {
  const invalidateRpcQueryFn = useInvalidateRpcQueryFn((orpc) => [
    orpc.accountsPayable.invoices.list.queryKey({ input: {} }),
  ]);
  const { showToast } = useToast();

  return useMutation(
    orpc.accountsPayable.invoices.delete.mutationOptions({
      onSuccess: (data, variables) => {
        if (data.success) {
          showToast({
            title: 'Success',
            message: 'Invoice deleted successfully',
            color: 'success',
            icon: 'check',
          });

          invalidateRpcQueryFn({});

          options?.onSuccess?.(data, variables);
        } else {
          showToast({
            title: 'Error',
            message: data.error || 'Failed to delete invoice',
            color: 'error',
            icon: 'x',
          });
        }
      },
      onError: (error) => {
        showToast({
          title: 'Error',
          message: error.message || 'Failed to delete invoice',
          color: 'error',
          icon: 'x',
        });
      },
    }),
  );
}
